// Drawing App - Selection Tool & Transform

import { SelectionRect, SelectionMode, Point } from './types';
import { LayerManager } from './layer_manager';
import { CanvasEngine } from './canvas_engine';
import { HistoryManager } from './history_manager';

export class SelectionManager {
  private selection: SelectionRect | null = null;
  private mode: SelectionMode = 'rectangle';
  private isSelecting = false;
  private isDragging = false;
  private isResizing = false;
  private startPoint: { x: number; y: number } | null = null;
  private dragOffset = { x: 0, y: 0 };
  private selectionCanvas: OffscreenCanvas | null = null;
  private onChangeCallback: (() => void) | null = null;

  setMode(mode: SelectionMode): void {
    this.mode = mode;
  }

  getMode(): SelectionMode {
    return this.mode;
  }

  getSelection(): SelectionRect | null {
    return this.selection ? { ...this.selection } : null;
  }

  onChange(cb: () => void): void {
    this.onChangeCallback = cb;
  }

  private notifyChange(): void {
    if (this.onChangeCallback) this.onChangeCallback();
  }

  startSelection(point: Point): void {
    // Check if clicking inside existing selection
    if (this.selection && this.isPointInSelection(point.x, point.y)) {
      this.isDragging = true;
      this.dragOffset = {
        x: point.x - this.selection.x,
        y: point.y - this.selection.y
      };
      return;
    }

    this.isSelecting = true;
    this.startPoint = { x: point.x, y: point.y };
    this.selection = { x: point.x, y: point.y, width: 0, height: 0, rotation: 0 };
  }

  updateSelection(point: Point): void {
    if (this.isDragging && this.selection) {
      this.selection.x = point.x - this.dragOffset.x;
      this.selection.y = point.y - this.dragOffset.y;
      this.notifyChange();
      return;
    }

    if (!this.isSelecting || !this.startPoint) return;

    this.selection = {
      x: Math.min(this.startPoint.x, point.x),
      y: Math.min(this.startPoint.y, point.y),
      width: Math.abs(point.x - this.startPoint.x),
      height: Math.abs(point.y - this.startPoint.y),
      rotation: 0
    };
    this.notifyChange();
  }

  endSelection(): void {
    this.isSelecting = false;
    this.isDragging = false;
    this.startPoint = null;

    // Remove selection if too small
    if (this.selection && (this.selection.width < 3 || this.selection.height < 3)) {
      this.selection = null;
    }
    this.notifyChange();
  }

  clearSelection(): void {
    this.selection = null;
    this.selectionCanvas = null;
    this.notifyChange();
  }

  cutSelection(layerManager: LayerManager, historyManager: HistoryManager): void {
    if (!this.selection) return;
    const layer = layerManager.getActiveLayer();
    if (!layer) return;

    const snapshot = layerManager.getLayerSnapshot(layer.id)!;
    const ctx = layer.canvas.getContext('2d')!;
    const { x, y, width, height } = this.selection;

    // Copy selection content
    this.selectionCanvas = new OffscreenCanvas(width, height);
    const selCtx = this.selectionCanvas.getContext('2d')!;
    selCtx.drawImage(layer.canvas, x, y, width, height, 0, 0, width, height);

    // Clear the area on the layer
    ctx.clearRect(x, y, width, height);

    const layerId = layer.id;
    const newSnapshot = layerManager.getLayerSnapshot(layerId)!;
    historyManager.push({
      type: 'cut',
      undo: () => layerManager.restoreLayerSnapshot(layerId, snapshot),
      redo: () => layerManager.restoreLayerSnapshot(layerId, newSnapshot)
    });
    this.notifyChange();
  }

  pasteSelection(layerManager: LayerManager, canvasEngine: CanvasEngine, historyManager: HistoryManager): void {
    if (!this.selectionCanvas || !this.selection) return;
    const layer = layerManager.getActiveLayer();
    if (!layer) return;

    const snapshot = layerManager.getLayerSnapshot(layer.id)!;
    const ctx = layer.canvas.getContext('2d')!;
    ctx.drawImage(this.selectionCanvas, this.selection.x, this.selection.y);

    const layerId = layer.id;
    const newSnapshot = layerManager.getLayerSnapshot(layerId)!;
    historyManager.push({
      type: 'paste',
      undo: () => layerManager.restoreLayerSnapshot(layerId, snapshot),
      redo: () => layerManager.restoreLayerSnapshot(layerId, newSnapshot)
    });
    canvasEngine.requestRender();
  }

  isPointInSelection(x: number, y: number): boolean {
    if (!this.selection) return false;
    const s = this.selection;
    return x >= s.x && x <= s.x + s.width && y >= s.y && y <= s.y + s.height;
  }

  drawSelectionOverlay(ctx: CanvasRenderingContext2D, transform: { offsetX: number; offsetY: number; scale: number }): void {
    if (!this.selection) return;

    const s = this.selection;
    ctx.save();
    ctx.translate(transform.offsetX, transform.offsetY);
    ctx.scale(transform.scale, transform.scale);

    // Marching ants border
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1 / transform.scale;
    ctx.strokeRect(s.x, s.y, s.width, s.height);

    ctx.setLineDash([6, 4]);
    ctx.lineDashOffset = 5;
    ctx.strokeStyle = '#fff';
    ctx.strokeRect(s.x, s.y, s.width, s.height);

    // Resize handles
    const handleSize = 6 / transform.scale;
    ctx.setLineDash([]);
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1 / transform.scale;

    const handles = [
      [s.x, s.y], [s.x + s.width / 2, s.y], [s.x + s.width, s.y],
      [s.x, s.y + s.height / 2], [s.x + s.width, s.y + s.height / 2],
      [s.x, s.y + s.height], [s.x + s.width / 2, s.y + s.height], [s.x + s.width, s.y + s.height]
    ];

    for (const [hx, hy] of handles) {
      ctx.fillRect(hx - handleSize / 2, hy - handleSize / 2, handleSize, handleSize);
      ctx.strokeRect(hx - handleSize / 2, hy - handleSize / 2, handleSize, handleSize);
    }

    ctx.restore();
  }
}
